import { ReactNode } from "react";
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ReportDecision, ReportLine, ReportRisk } from "../../../../utils/icGovernance";
import { useViewport } from "../../../../utils/useViewport";
import FieldGrid from "../detail/FieldGrid";
import { GovSettingsOrNull } from "../detail/types";
import { FAINT, MONO, MUTED, TEXT } from "../detail/ui";
import { dash, dateOnly, itemUrlById, partyLabel } from "../governanceStatus";

/** A report line's text, prefixed with its item's display id as a link. */
const LineText = ({ line }: { line: ReportLine }) => (
  <>
    {line.displayId ? (
      <MuiLink
        component={RouterLink}
        to={itemUrlById(line.displayId)}
        sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13, mr: 1 }}
      >
        {line.displayId}
      </MuiLink>
    ) : null}
    <Box component="span" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
      {line.text}
    </Box>
  </>
);

const Empty = () => (
  <Typography variant="body2" sx={{ color: FAINT }}>
    Nothing listed.
  </Typography>
);

/** Completed This Week / Planned Next Week. */
export const ReportLines = ({ lines }: { lines: ReportLine[] }) => {
  if (!lines?.length) return <Empty />;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {lines.map((line, i) => (
        <Typography component="li" variant="body2" key={`${i}-${line.displayId ?? ""}`} sx={{ color: TEXT, py: 0.25 }}>
          <LineText line={line} />
        </Typography>
      ))}
    </Box>
  );
};

const headSx = { fontWeight: 700, color: MUTED, fontSize: 12, whiteSpace: "nowrap" } as const;
const cellSx = { color: TEXT, verticalAlign: "top", overflowWrap: "anywhere" } as const;

interface Column<T> {
  label: string;
  render: (row: T) => ReactNode;
  width?: string;
}

/** A table from tablet width up, one small card per row on phones. */
const RowsTable = <T,>({ rows, columns, label }: { rows: T[]; columns: Column<T>[]; label: string }) => {
  const { isPhone } = useViewport();
  if (!rows?.length) return <Empty />;
  if (isPhone) {
    return (
      <Stack spacing={1.25}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ border: "1px solid #f0f0f3", borderRadius: "10px", p: 1.5 }}>
            <FieldGrid fields={columns.map((c) => ({ label: c.label, value: c.render(row) }))} columns={1} />
          </Box>
        ))}
      </Stack>
    );
  }
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.label} sx={{ ...headSx, width: c.width }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c.label} sx={cellSx}>
                  {c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DECISION_COLUMNS: Column<ReportDecision>[] = [
  { label: "Decision", render: (d) => <LineText line={d} /> },
  { label: "Decision Owner", render: (d) => dash(d.owner), width: "22%" },
  { label: "Decision Due", render: (d) => dateOnly(d.dueDate), width: "16%" },
];

/** Decision Needed: Decision · Decision Owner · Decision Due. */
export const DecisionsTable = ({ decisions }: { decisions: ReportDecision[] }) => (
  <RowsTable rows={decisions} columns={DECISION_COLUMNS} label="Decisions needed" />
);

/** Risk / Blocker: Risk / Blocker · Mitigation / Next Action · Owner Party. */
export const RisksTable = ({ risks, settings }: { risks: ReportRisk[]; settings: GovSettingsOrNull }) => (
  <RowsTable
    rows={risks}
    label="Risks and blockers"
    columns={[
      { label: "Risk / Blocker", render: (r) => <LineText line={r} /> },
      {
        label: "Mitigation / Next Action",
        render: (r) => (
          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
            {dash(r.mitigation)}
          </Box>
        ),
      },
      { label: "Owner Party", render: (r) => partyLabel(r.ownerParty, settings), width: "16%" },
    ]}
  />
);
